import React from "react";
import { SelectionStats, Survey, rng, segmentListIsInLists } from '../../../amorg-common';
import { Card } from "../../../designSystem/Card";
import { H2, H3 } from "../../../designSystem/typography/Heading";
import { Button } from "../../../designSystem/button/Button";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { getActionPlansByActivityId, createActionPlan } from "../../../api/actionPlanClientApi";
import { WorkplaceAssessmentComponentProps } from './WorkplaceAssessmentComponentProps';
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { SurveyReport } from '../../../amorg-common';
import { SurveySegment } from '../../../amorg-common';
import { segmentListsMatch } from '../../../amorg-common';
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { P } from "../../../designSystem/typography/Body";
import { ActionPlanListView } from "../../../amorg-common/DTOs/ActionPlanListView";
import { AmorgMenu, AmorgMenuContent } from "../../../designSystem/amorgMenu";
import { MessageCard } from "../../../designSystem/Message";
import { RadialPctChart } from "../../../designSystem/graphs/RadialPctChart";
import { PageHeader } from "../../../designSystem/PageHeader";
import { PageCardSection } from "../../../designSystem/PageSection";
import { Icon } from "../../../designSystem/icons/Icons";
import { colors } from "../../../designSystem/colors/color";
import { ReadOnlySegmentPanel } from "../../surveys/segmentation/ReadOnlySegmentPanel";
import { CardButton } from "../../../designSystem/CardButton";

type State = {
    actionPlansLoading: boolean,
    actionPlans: ActionPlanListView[],
    segmentsWithoutPlans: SurveySegment[][];
};

type ActionPlansPanelListingProps = WorkplaceAssessmentComponentProps &
{
    onActionPlanSelected: (actionPlanId: string) => void;
};

export function ActionPlansPanelListing (props: ActionPlansPanelListingProps): JSX.Element
{

    const [state, setState] = React.useState<State>(
        {
            actionPlansLoading: true,
            actionPlans: [],
            segmentsWithoutPlans: []
        }
    );

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            await loadData();
        };
        getData();
    }, []);

    async function loadData ()
    {
        const actionPlans = await getActionPlansByActivityId(props.activity.id);

        setState({
            actionPlansLoading: false,
            actionPlans,
            segmentsWithoutPlans: props.surveyReport
                ? getSegmentsWithoutPlans(props.surveyReport, actionPlans)
                : []
        });
    }

    function getSegmentsWithoutPlans (surveyReport: SurveyReport, actionPlans: ActionPlanListView[])
    {
        const segmentsWithoutPlans: SurveySegment[][] = [];
        for (const cr of surveyReport.selectionReports)
        {
            let matched = false;
            for (const ap of actionPlans)
            {
                if (ap.type === 'WPA')
                {
                    for (const selectionList of ap.segmentFilter)
                    {
                        if (segmentListsMatch(selectionList, cr.segments))
                        {
                            matched = true;
                            break;
                        }
                    }
                    if (matched)
                        break;
                }
            }
            if (!matched)
                segmentsWithoutPlans.push(cr.segments);
        }
        return segmentsWithoutPlans;
    }

    async function handleClickCreateActionPlan ()
    {
        const result = await createActionPlan(rng.getRandomId(), props.activity.id, 'WPA');
        if (result.isSuccess)
        {
            await loadData();
        }
    }

    let completionPct = 0;
    if (props.survey && state.actionPlansLoading === false)
    {
        const allSegmentedAnswers = props.surveyReport.selectionReports.map(sr =>
        ({
            segments: sr.segments,
            answerCount: sr.questionIndexedSummaries.length === 0
                ? 0
                : new SelectionStats(sr.questionIndexedSummaries[0].selectionSummary).totalSelections
        })
        );

        const segmentsWithActionplans = allSegmentedAnswers.filter(sa =>
            !segmentListIsInLists(state.segmentsWithoutPlans, sa.segments)
        );

        const allAnswers = allSegmentedAnswers.reduce((acc, sa) => { return acc + sa.answerCount; }, 0);
        const answersWithActionplans = segmentsWithActionplans.reduce((acc, sa) => { return acc + sa.answerCount; }, 0);

        completionPct = answersWithActionplans / allAnswers * 100;
    }

    return (
        <>
            {state.actionPlansLoading &&
                <LoadingPageOverlay loading={true} />
            }

            <PageHeader
                headerText="Handleplaner" headerStyle="secondary-gray" />



            {(props.survey === null || props.survey.segmentations.length === 0) &&
                <>
                    {state.actionPlans.length === 0 &&
                        <MessageCard variant="info">
                            Der er ikke oprettet nogen handleplaner.<br />Tilføj en ny handleplan for at komme i gang.
                        </MessageCard>
                    }
                </>
            }

            {props.survey && props.survey.segmentations.length > 0 &&

                <PageCardSection
                    header={<H2>Status</H2>}>

                    <Flex gap={10}>
                        <Flex flexDirection="column" justifyContent="flex-start">
                            <RadialPctChart pct={completionPct} />
                        </Flex>
                        <Flex flexDirection="column" gap={20} style={{flexGrow:1}}>
                            <P>
                                Spørgeskemaets besvarelser er inddelt i segmenteringer, baseret på de segmenter respondenten har valgt.<br />
                                Vi måler hvor mange procent af spørgeskemaets segmenteringer der er omfattet af en handleplan.
                            </P>
                            {state.actionPlans.length === 0 &&
                                <>
                                    <P>
                                        Da der endnu ikke er oprettet handleplaner, viser grafen 0%.
                                    </P>
                                    <P>
                                        Når der er oprettet handleplaner, vil grafen vise hvor langt I er kommet med at afdække hele APV&apos;en med handleplaner.
                                    </P>
                                </>
                            }

                            {completionPct > 0 && completionPct < 100 &&
                                <P>
                                    Der mangler fortsat at blive oprettet handleplaner for en eller flere af spørgeskemaets besvarelser.
                                </P>
                            }

                            {completionPct > 99.9 &&
                                <Flex gap={10}>
                                    <Icon name="actions_done" color={colors.graphs.survey.answers.verySatisfied} />
                                    <P>
                                        Alle spørgeskemaets besvarelser er omfattet af handleplaner!
                                    </P>
                                </Flex>
                            }

                            {completionPct < 100 &&
                                <>
                                    <P>
                                        Følgende segmenter er endnu ikke omfattet af en handleplan
                                    </P>
                                    <ReadOnlySegmentPanel segmentLists={state.segmentsWithoutPlans} lockedView="pills" />
                                </>
                            }
                        </Flex>
                    </Flex>

                </PageCardSection>
            }

            <Flex flexDirection="column" gap={16}>

                {state.actionPlans.map(ap =>
                    <ActionPlanListing key={ap.id} survey={props.survey} actionPlan={ap} onClick={props.onActionPlanSelected} />
                )}

                <ButtonRow variant="stretched">
                    <Button
                        variant='light'
                        size='magnum'
                        onClick={handleClickCreateActionPlan}
                        text={"Opret handleplan"}
                        icon="actions_plus"
                    />
                </ButtonRow>
            </Flex>
        </>
    );
}


type ActionPlanListingProps = {
    survey: Survey | null,
    actionPlan: ActionPlanListView,
    onClick: (actionPlanId: string) => void;
};
function ActionPlanListing (props: ActionPlanListingProps): JSX.Element
{
    const ap = props.actionPlan;

    let actionPointMenuItem: AmorgMenuContent =
    {
        type: "leadingTrailingContent",
        styleVariant: "destructive",
        leadingContent: { type: "icon", iconName: 'actions_done' },
        primaryContent: { type: 'text', text: "Ingen handlepunkter er oprettet" },
        trailingContent: { type: "none" },
    };

    if (ap.actionpointStats.total > 0)
    {
        if (ap.actionpointStats.pastDeadline > 0)
        {
            actionPointMenuItem = {
                type: "leadingTrailingContent",
                styleVariant: "destructive",
                leadingContent: { type: "icon", iconName: 'interface_actions_notificationOn' },
                primaryContent: { type: 'text', text: `${ap.actionpointStats.pastDeadline + ap.actionpointStats.pending} ud af ${ap.actionpointStats.total} handlepunkter er åbne` },
                trailingContent: { type: 'text', text: ap.actionpointStats.pastDeadline === 1 ? '1 deadline er overskredet' : `${ap.actionpointStats.pastDeadline} deadlines er overskredet` }
            };
        }
        else if (ap.actionpointStats.completed === ap.actionpointStats.total)
        {
            actionPointMenuItem = {
                type: "leadingContent",
                styleVariant: "default",
                leadingContent: { type: "icon", iconName: 'actions_done' },
                primaryContent: { type: 'text', text: `Alle (${ap.actionpointStats.total}) handlepunkter er afsluttet` }
            };
        }
        else
        {
            actionPointMenuItem = {
                type: "leadingContent",
                styleVariant: "default",
                leadingContent: { type: "icon", iconName: 'actions_done' },
                primaryContent: { type: 'text', text: `${ap.actionpointStats.pending} ud af ${ap.actionpointStats.total} handlepunkter er åbne` }
            };
        }

    }

    return (
        <CardButton marginTop={10} onClick={() => props.onClick(props.actionPlan.id)}>
            <Flex flexDirection="column" gap={16}>

                <div>
                    <H3>{ap.title}</H3>
                    <P>
                        {ap.description}
                    </P>
                </div>

                {ap.type === 'WPA' &&
                    <>
                        {ap.segmentFilter.length > 0 &&
                            <Card variant="outlined">
                                <Flex flexDirection="column" gap={5}>
                                    <P>Handleplanen er tilknyttet spørgeskemaebesvarelserne for følgende segmenter</P>
                                    <ReadOnlySegmentPanel segmentLists={ap.segmentFilter} lockedView="pills" />
                                </Flex>
                            </Card>
                        }

                        {props.survey && props.survey.segmentations.length > 0 && ap.segmentFilter.length === 0 &&
                            <P>Handleplanen er ikke tilknyttet besvarelser for specifikke segmenter i spørgeskemaet</P>
                        }
                    </>
                }

                <AmorgMenu
                    content={[
                        {
                            type: "leadingContent",
                            styleVariant: ap.owner ? "default" : "destructive",
                            leadingContent: { type: "icon", iconName: "interface_user_account" },
                            primaryContent: { type: 'text', text: ap.owner ? `${ap.owner.fullName} er ejer af handleplanen` : "Ingen ejer af handleplanen er valgt" }
                        },
                        {
                            type: "leadingContent",
                            styleVariant: ap.topicCount > 0 ? "default" : "destructive",
                            leadingContent: { type: "icon", iconName: "tags_tag" },
                            primaryContent: {
                                type: 'text',
                                text: ap.topicCount === 0
                                    ? 'Ingen emner oprettet!'
                                    : (ap.topicCount === 1 ? '1 emne er oprettet' : `${ap.topicCount} emner er oprettet`)
                            }
                        },
                        actionPointMenuItem
                    ]} />
            </Flex>
        </CardButton>
    );
}